import { Fragment, type ReactNode } from 'react';
import type { TypeTokens } from '../tokens';

export interface LabelledRow {
  key: string;
  label: ReactNode;
  value: ReactNode;
  /** Commentary on the value, demoted to grey beneath it. */
  note?: ReactNode;
}

/**
 * A named value per row, names in a column of their own.
 *
 * The shape both halves of a finding's apparatus want. A description block is a lead and the figures
 * that go with it; a source is a label and the statement it produced — in each case the names are
 * there to be scanned down and the values to be read across, which is what a column does and a
 * stack of label-above-value does not.
 *
 * Stacks on a narrow viewport, where a fixed name column would leave the values a few words wide.
 */
export const LabelledRows = ({
  type,
  measure,
  rows,
  testId,
}: {
  type: Pick<TypeTokens, 'detail' | 'detailLabel' | 'body'>;
  measure: string;
  rows: LabelledRow[];
  testId?: string;
}) => (
  <dl
    data-testid={testId}
    className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[10rem_1fr] sm:gap-y-5"
  >
    {rows.map((row) => (
      <Fragment key={row.key}>
        <dt className={`${type.detailLabel} sm:pt-px`}>{row.label}</dt>
        <dd className="flex min-w-0 flex-col gap-1">
          <span className={`${type.detail} ${measure}`}>{row.value}</span>
          {row.note ? <span className={type.body}>{row.note}</span> : null}
        </dd>
      </Fragment>
    ))}
  </dl>
);
